import { Equipment } from "@/services/project/equipment/equipment.model"
import { Edge, EdgeView, Graph, Node, PointLike } from "@antv/x6"
import { PortManager } from "@antv/x6/lib/model/port"
import { cellToNodeState } from "./storage"
import { PortTypes } from "../constant"
import { NozzleRecord } from "./modal"
import { getNodePortPosition } from "../events/helper"
import { NozzleType } from "@/types/common"


export function addNozzleToNozzleEdge(newNozzleId: string, nozzleEdge: Equipment.NozzleEdge, node: Node, graph: Graph) {
  const nodeData = cellToNodeState(node.toJSON())
  const nozzles = nodeData.nozzles.filter(item => item.name.startsWith(nozzleEdge.simCode))
  const points = relayoutEdgeNozzles(graph, node, nozzleEdge, true)!

  const lastPoint = points[points.length - 1]
  const newPortData: NozzleRecord = {
    id: newNozzleId,
    name: `${nozzleEdge.simCode}${nozzles.length}`,
    direction: nozzleEdge.direction,
    type: NozzleType.FlowNozzle,
    group: PortTypes.NozzlePort,
    layout: {
      ...lastPoint
    },
    nozzleEdge: true,
    nozzleEdgeName: nozzleEdge.name,
  }
  const newPort = {
    id: newNozzleId,
    group: PortTypes.NozzlePort,
    data: newPortData,
    args: {...lastPoint}
  }
  node.addPort(newPort)
  node.trigger('cell:changed', {cell: node})
}


interface Line {
  start: PointLike,
  end: PointLike
}

function getPointsOnLine(line: Line, count: number): PointLike[] {
  if (count < 1) {
    throw new Error('Count must be greater than 0');
  }

  const { start, end } = line;

  if (count === 1) {
    return [{
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2
    }];
  }

  const points: PointLike[] = [];
  
  const step = 1 / (count - 1);

  for (let i = 0; i < count; i++) {
    const t = i * step;
    
    const x = start.x + (end.x - start.x) * t;
    const y = start.y + (end.y - start.y) * t;

    points.push({ x, y });
  }

  return points;
}

export function changeNodePortPosition(node: Node, port: PortManager.PortMetadata, offset: PointLike, graph: Graph, nozzleEdgeLine: Line) {
  let isSource = false
  const portId = port.id!
  node.portProp(portId, 'data/layout', port.data.layout)
  const {x, y} = port.data.layout;
  node.portProp(portId, 'args/x', x)
  node.portProp(portId, 'args/y', y)
  const line = graph.getEdges().find(item => {
    const source = item.getSource() as any
    const target = item.getTarget() as any
    const isPortConnectToSource = source.cell === node.id && source.port === portId
    if (isPortConnectToSource) {
      isSource = true
    }
    return isPortConnectToSource ||
      (target.cell === node.id && target.port === portId)
  })
  const nodePos = node.getPosition()
  const newPos = {
    x: nodePos.x + offset.x,
    y: nodePos.y + offset.y
  }
  if (line) {
    adjustLinePoint(graph, line, newPos, isSource, nozzleEdgeLine)
    graph.trigger('edge:changed', {edge: line})
  }
}


function adjustLinePoint(graph: Graph, line: Edge, point: PointLike, isSource: boolean, nozzleEdgeLine: Line) {
  const points = line.getVertices()
  if (isSource) {
    const nextPoint = points[1] || getLineEndPoint(graph, line, false);
    if (nextPoint) {
      const perpendicularPoint = findProjectionPoint(nozzleEdgeLine.start, nozzleEdgeLine.end, point, nextPoint)
      const newPoints = [perpendicularPoint, ...points.slice(1)]
      line.setVertices(newPoints)
    }
  } else {
    const prevPoint = points[points.length - 2] || getLineEndPoint(graph, line, true);
    if (prevPoint) {
      const perpendicularPoint = findProjectionPoint(nozzleEdgeLine.start, nozzleEdgeLine.end, point, prevPoint)
      const newPoints = [...points.slice(0, -1), perpendicularPoint]
      line.setVertices(newPoints)
    }
  }
}

export function getLineEndPoint(graph: Graph, line: Edge, isSource: boolean): PointLike {
  const endPoint: any = isSource ? line.getSource() : line.getTarget()
  if (endPoint.x !== undefined && endPoint.y !== undefined) {
    return endPoint as PointLike
  } else if(endPoint.cell !== undefined && endPoint.port !== undefined) {
    const node = graph.getCellById(endPoint.cell) as Node
    return getNodePortPosition(node, endPoint.port)!;
  } else if(endPoint.cell !== undefined && endPoint.anchor !== undefined) {
    const edge = graph.getCellById(endPoint.cell) as Edge
    const anchorLenght = endPoint.anchor.args.length
    const edgeView = <EdgeView>graph.findViewByCell(edge)
    return edgeView.getPointAtLength(anchorLenght) as PointLike
  }
  throw new Error("Invalid line end point");
}

function findProjectionPoint(
  pointA: PointLike,
  pointB: PointLike,
  pointC: PointLike,
  pointD: PointLike
): PointLike {
  // 1. 计算CD的中点
  const midPointCD: PointLike = {
    x: (pointC.x + pointD.x) / 2,
    y: (pointC.y + pointD.y) / 2
  };

  // 2. 计算AB的方向向量
  const vectorAB = {
    x: pointB.x - pointA.x,
    y: pointB.y - pointA.y
  };

  // 3. 计算AB的单位向量
  const lengthAB = Math.sqrt(vectorAB.x * vectorAB.x + vectorAB.y * vectorAB.y);
  const unitAB = {
    x: vectorAB.x / lengthAB,
    y: vectorAB.y / lengthAB
  };

  // 4. 计算AB的垂直单位向量 (逆时针旋转90度)
  const perpendicular = {
    x: -unitAB.y,
    y: unitAB.x
  };

  // 5. 计算从C点到CD中点的向量
  const vectorCM = {
    x: midPointCD.x - pointC.x,
    y: midPointCD.y - pointC.y
  };

  // 6. 计算投影长度（点积）
  const projection = vectorCM.x * perpendicular.x + vectorCM.y * perpendicular.y;

  // 7. 计算投影点
  const result: PointLike = {
    x: pointC.x + perpendicular.x * projection,
    y: pointC.y + perpendicular.y * projection
  };

  return result;
}


export function removeEdgeNozzleFromNode(node: Node, port: PortManager.PortMetadata | undefined, graph: Graph) {
  if (!port) {
    return;
  }
  node.removePort(port)
  const nozzleEdge = node.getData().nozzleEdges.find(item => item.name === port.data.nozzleEdgeName)
  relayoutEdgeNozzles(graph, node, nozzleEdge, false)
}

function getNozzleEdgeLine(node: Node, nozzleEdge: Equipment.NozzleEdge): Line {
  const {start, end} = nozzleEdge;
  const {width, height} = node.getSize()
  const startPoint = {
    x: start.x * width,
    y: start.y * height
  }
  const endPoint = {
    x: end.x * width,
    y: end.y * height
  }
  return {start: startPoint, end: endPoint}
}

function layoutNodeNozzlePoints(
  node: Node,
  nozzleEdge: Equipment.NozzleEdge,
  addOne: boolean = false
): PointLike[] {
  const nodeData = cellToNodeState(node.toJSON())
  const nozzles = nodeData.nozzles.filter(item => item.name.startsWith(nozzleEdge.simCode))
  const edgeNozzles = nozzles.filter(item => item.nozzleEdgeName === nozzleEdge.name)
  const nozzleEdgeLine = getNozzleEdgeLine(node, nozzleEdge)
  const count = addOne ? edgeNozzles.length + 1 : edgeNozzles.length
  if (count < 1) {
    return []
  }

  return getPointsOnLine(nozzleEdgeLine, count)
}

function relayoutEdgeNozzles(
  graph: Graph,
  node: Node,
  nozzleEdge: Equipment.NozzleEdge,
  addOne: boolean = false
) {
  const nodeData = cellToNodeState(node.toJSON())
  const nozzles = nodeData.nozzles.filter(item => item.name.startsWith(nozzleEdge.simCode))
  const edgeNozzles = nozzles.filter(item => item.nozzleEdgeName === nozzleEdge.name)
  const points = layoutNodeNozzlePoints(node, nozzleEdge, addOne)
  if (points.length === 0) {
    return null;
  }
  const nozzleEdgeLine = getNozzleEdgeLine(node, nozzleEdge)
  
  edgeNozzles.map((nozzle, index) => {
    const layout = points[index]
    nozzle.layout = {...nozzle.layout!,
      x: layout.x / nodeData.size.width,
      y: layout.y / nodeData.size.height,
    }
    return {
      id: nozzle.id,
      group: PortTypes.NozzlePort,
      data: nozzle,
      args: {...layout}
    } as PortManager.PortMetadata
  }).forEach(item => {
    changeNodePortPosition(node, item, item.args as any, graph, nozzleEdgeLine)
  })

  if (!addOne) {
    return null
  }

  return points
}